import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface UatRow {
  readonly uatPlanId: string;
  readonly uatCode: string;
  readonly title: string;
  readonly status: string;
  readonly goLiveRecommendation: string;
}

const STATUSES = ['', 'PLANNED', 'IN_PROGRESS', 'COMPLETED', 'SIGNED_OFF', 'REJECTED'];
const RECOMMENDATIONS = ['', 'GO', 'CONDITIONAL_GO', 'NO_GO', 'PENDING'];
const DECISIONS = ['GO', 'CONDITIONAL_GO', 'NO_GO'];

/**
 * M34 — Sign-Off Management (§31 / §19). Governance view over UAT plans (T456)
 * and their sign-off outcome (T457). BR-03: UAT approval is mandatory before
 * release certification. Records the business-owner sign-off decision against a
 * COMPLETED plan via the guarded /v1/qms/uat/:id/signoff endpoint (segregated
 * UAT_APPROVE permission, §30 SoD). Server-filtered by UAT status and further
 * narrowed by the go-live recommendation on the loaded set.
 */
export function SignOffManagement(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<UatRow[]>([]);
  const [status, setStatus] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: UatRow[] }>(`/v1/qms/uat${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const visible = useMemo(
    () => (recommendation ? rows.filter((r) => r.goLiveRecommendation === recommendation) : rows),
    [rows, recommendation],
  );

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const signOff = async (r: UatRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/uat/${r.uatPlanId}/signoff`, {
        method: 'POST',
        body: { decision: decisions[r.uatPlanId] ?? 'GO' },
        token,
      });
      setNotice(t('m34.signoff.signedOff', { code: r.uatCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_UAT_APPROVE'));
    }
  };

  return (
    <main aria-label="signoff-management">
      <h1>{t('m34.signoff.title')}</h1>
      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.signoff.recommendation')}
          <select aria-label="filter-recommendation" value={recommendation} onChange={(e) => setRecommendation(e.target.value)}>
            {RECOMMENDATIONS.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {actionError && <p role="alert" aria-label="signoff-action-error">{actionError}</p>}
      {notice && <p aria-label="signoff-action-notice">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="signoff-count">{t('m34.signoff.count', { n: visible.length })}</p>
          <DataTable<UatRow>
            ariaLabel="signoff-table"
            rowKey={(r) => r.uatPlanId}
            rows={visible}
            rowDataAttrs={(r) => ({ 'data-recommendation': r.goLiveRecommendation })}
            columns={[
              { key: 'uatCode', header: t('m34.signoff.colPlan') },
              { key: 'title', header: t('m34.common.title') },
              { key: 'status', header: t('m34.common.status') },
              { key: 'goLiveRecommendation', header: t('m34.signoff.recommendation') },
              {
                key: 'actions',
                header: t('m34.common.actions'),
                render: (r) => (
                  r.status === 'COMPLETED' && (
                    <>
                      <select aria-label={`decision-${r.uatCode}`} value={decisions[r.uatPlanId] ?? 'GO'} onChange={(e) => setDecisions((m) => ({ ...m, [r.uatPlanId]: e.target.value }))}>
                        {DECISIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <Button variant="secondary" type="button" aria-label={`signoff-${r.uatCode}`} onClick={() => void signOff(r)}>{t('m34.signoff.signOffBtn')}</Button>
                    </>
                  )
                ),
              },
            ]}
          />
        </>
      )}
    </main>
  );
}
